// src/pages/CAActsList.jsx - Optimized with Infinite Scroll
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SearchNavigation from '../components/SearchNavigation';
import LeApiService from '../services/leApiService';

const CAActsList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [allActs, setAllActs] = useState([]); // All data from API
  const [displayedActs, setDisplayedActs] = useState([]); // Currently displayed items
  const [searchText, setSearchText] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  const observerTarget = useRef(null);
  const ITEMS_PER_PAGE = 50; // Load 50 items at a time

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setIsLoading(true);
      console.log('🔄 Fetching Central Acts...');
      
      const apiResponse = await LeApiService.getCAActList();
      console.log('✅ Total acts loaded:', apiResponse?.length || 0);
      
      setAllActs(apiResponse || []);
      
      // Load first batch
      const initialBatch = (apiResponse || []).slice(0, ITEMS_PER_PAGE);
      setDisplayedActs(initialBatch);
      setHasMore((apiResponse || []).length > ITEMS_PER_PAGE);
      
    } catch (error) {
      console.error('❌ Error loading acts:', error);
      setError(error.message || 'Failed to load acts');
    } finally {
      setIsLoading(false);
    }
  }

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    
    setTimeout(() => {
      const currentLength = displayedActs.length;
      const filteredData = searchText 
        ? allActs.filter(item => item.toLowerCase().includes(searchText.toLowerCase()))
        : allActs;
      
      const nextBatch = filteredData.slice(
        currentLength, 
        currentLength + ITEMS_PER_PAGE
      );
      
      setDisplayedActs(prev => [...prev, ...nextBatch]);
      setHasMore(currentLength + ITEMS_PER_PAGE < filteredData.length);
      setIsLoadingMore(false);
    }, 300); // Small delay for smooth UX
  }, [displayedActs, allActs, searchText, hasMore, isLoadingMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMoreItems();
        }
      },
      { threshold: 0.5 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loadMoreItems, hasMore, isLoadingMore]);

  // Handle search with debounce
  const handleSearchChange = (value) => {
    setSearchText(value);
    
    // Reset to first batch with filtered results
    const filteredData = value 
      ? allActs.filter(item => item.toLowerCase().includes(value.toLowerCase()))
      : allActs;
    
    const initialBatch = filteredData.slice(0, ITEMS_PER_PAGE);
    setDisplayedActs(initialBatch);
    setHasMore(filteredData.length > ITEMS_PER_PAGE);
  };

  const handleOpenPdf = (post) => {
    const url = `https://legaleagleweb.com/CompleteActPDF/${post}.pdf`;
    setTitle(post);
    setPdfUrl(url);
    setShowModal(true);
  };

  const filteredTotal = searchText 
    ? allActs.filter(item => item.toLowerCase().includes(searchText.toLowerCase())).length
    : allActs.length;

  if (isLoading) {
    return (
      <div className="acts-layout">
        <Navbar />
        <SearchNavigation />
        <div className="loading-fullscreen">
          <i className="bx bx-loader-alt bx-spin"></i>
          <p>Loading Central Laws...</p>
        </div>
        <style>{`
          .acts-layout { min-height: 100vh; background: #fafafa; }
          .loading-fullscreen {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 15px;
          }
          .loading-fullscreen i { font-size: 60px; color: #E91E63; }
          .loading-fullscreen p { font-size: 18px; color: #666; margin: 0; }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .bx-spin { animation: spin 1s linear infinite; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="acts-layout">
      <Navbar />
      <SearchNavigation />

      <div className="acts-main-content">
        {/* Hero Section */}
        <div className="acts-hero">
          <div className="hero-badge">
            <i className="bx bx-book-content"></i>
            <span>Central Acts</span>
          </div>
          <h1 className="hero-title">Central Laws of India</h1>
          <p className="hero-subtitle">Complete collection of Central Acts and legislations</p>
        </div>

        <div className="acts-container">
          {/* Search Card */}
          <div className="search-card">
            <div className="search-card-header">
              <div className="search-icon-wrapper">
                <i className="bx bx-search-alt"></i>
              </div>
              <div className="search-content">
                <h3 className="search-title">Search Central Acts</h3>
                <p className="search-subtitle">
                  Showing {displayedActs.length} of {filteredTotal} act{filteredTotal !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="search-card-body">
              <div className="search-box">
                <i className="bx bx-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by act name..."
                />
                {searchText && (
                  <button
                    className="clear-search"
                    onClick={() => handleSearchChange('')}
                  >
                    <i className="bx bx-x"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Acts List */}
          {error ? (
            <div className="error-section">
              <i className="bx bx-error-circle"></i>
              <p>{error}</p>
            </div>
          ) : displayedActs.length === 0 ? (
            <div className="empty-section">
              <i className="bx bx-search-alt"></i>
              <p>No acts found matching "{searchText}"</p>
              <button 
                className="clear-filter-btn"
                onClick={() => handleSearchChange('')}
              >
                Clear Search
              </button>
            </div>
          ) : (
            <>
              <div className="acts-grid">
                {displayedActs.map((post, index) => (
                  <div key={index} className="act-card">
                    <div className="act-card-inner">
                      <div className="act-icon">
                        <i className="bx bx-file-blank"></i>
                      </div>
                      <div className="act-content">
                        <h4 
                          className="act-title"
                          onClick={(e) => {
                            e.preventDefault();
                            handleOpenPdf(post);
                          }}
                        >
                          {post}
                        </h4>
                        <div className="act-meta">
                          <span className="act-tag">
                            <i className="bx bx-bookmark"></i>
                            Central Act
                          </span>
                        </div>
                      </div>
                      <div className="act-action">
                        <button
                          className="view-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            handleOpenPdf(post);
                          }}
                        >
                          <i className="bx bx-show"></i>
                          <span>View PDF</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Loading More Indicator */}
              {isLoadingMore && (
                <div className="loading-more">
                  <i className="bx bx-loader-alt bx-spin"></i>
                  <p>Loading more acts...</p>
                </div>
              )}

              {/* Intersection Observer Target */}
              {hasMore && <div ref={observerTarget} className="observer-target"></div>}

              {/* End of Results */}
              {!hasMore && displayedActs.length > 0 && (
                <div className="end-results">
                  <i className="bx bx-check-circle"></i>
                  <p>All {filteredTotal} acts loaded</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <i className="bx bx-file-blank"></i>
                <span>{title || 'Act Viewer'}</span>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <i className="bx bx-x"></i>
              </button>
            </div>
            <div className="modal-body">
              <iframe src={pdfUrl} className="modal-iframe" title="Act Viewer" />
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Base Layout */
        .acts-layout {
          min-height: 100vh;
          background: #fafafa;
        }

        .acts-main-content {
          padding-top: 140px;
          width: 100%;
        }

        /* Hero Section */
        .acts-hero {
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 25%, #c41849 75%, #D81B60 100%);
          padding: 50px 20px 40px;
          text-align: center;
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        .hero-badge i {
          font-size: 16px;
        }

        .hero-title {
          font-size: 2rem;
          font-weight: 800;
          margin: 0 0 10px 0;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1rem;
          opacity: 0.95;
          margin: 0;
          font-weight: 400;
        }

        /* Acts Container */
        .acts-container {
          max-width: 1200px;
          margin: -30px auto 0;
          padding: 0 20px 40px;
          position: relative;
          z-index: 10;
        }

        /* Search Card */
        .search-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          margin-bottom: 30px;
        }

        .search-card-header {
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          padding: 24px 28px;
          display: flex;
          align-items: center;
          gap: 16px;
          border-bottom: 2px solid #f5f5f5;
        }

        .search-icon-wrapper {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          flex-shrink: 0;
          box-shadow: 0 4px 15px rgba(245, 166, 35, 0.3);
        }

        .search-content {
          flex: 1;
        }

        .search-title {
          font-size: 20px;
          font-weight: 700;
          color: #333;
          margin: 0 0 4px 0;
        }

        .search-subtitle {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        .search-card-body {
          padding: 24px 28px;
        }

        .search-box {
          position: relative;
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          font-size: 20px;
        }

        .search-input {
          width: 100%;
          padding: 16px 50px 16px 55px;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          font-size: 15px;
          color: #333;
          background: #fafafa;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #E91E63;
          background: white;
          box-shadow: 0 0 0 4px rgba(245, 166, 35, 0.1);
        }

        .clear-search {
          position: absolute;
          right: 18px;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border: none;
          background: #f0f0f0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
        }

        .clear-search:hover {
          background: #e0e0e0;
          color: #333;
        }

        .clear-search i {
          font-size: 20px;
        }

        /* Error & Empty States */
        .error-section,
        .empty-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 15px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        }

        .error-section i,
        .empty-section i {
          font-size: 60px;
          color: #E91E63;
        }

        .error-section i {
          color: #c41849;
        }

        .error-section p,
        .empty-section p {
          font-size: 16px;
          color: #666;
          margin: 0;
          text-align: center;
        }

        .clear-filter-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 10px;
        }

        .clear-filter-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(233, 30, 99, 0.3);
        }

        /* Acts Grid */
        .acts-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
        }

        .act-card {
          background: white;
          border: 2px solid #f0f0f0;
          border-radius: 12px;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .act-card:hover {
          border-color: #E91E63;
          box-shadow: 0 6px 20px rgba(245, 166, 35, 0.15);
          transform: translateY(-2px);
        }

        .act-card-inner {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 20px 24px;
        }

        .act-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.15), rgba(233, 30, 99, 0.1));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #E91E63;
          font-size: 24px;
          flex-shrink: 0;
        }

        .act-content {
          flex: 1;
          min-width: 0;
        }

        .act-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin: 0 0 8px 0;
          cursor: pointer;
          transition: all 0.2s ease;
          line-height: 1.4;
        }

        .act-title:hover {
          color: #E91E63;
        }

        .act-meta {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .act-tag {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 12px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
          color: #E91E63;
        }

        .act-tag i {
          font-size: 14px;
        }

        .act-action {
          flex-shrink: 0;
        }

        .view-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: linear-gradient(135deg, #E91E63, #c41849);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .view-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.4);
        }

        .view-btn i {
          font-size: 20px;
        }

        /* Loading More Indicator */
        .loading-more {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          gap: 12px;
        }

        .loading-more i {
          font-size: 40px;
          color: #E91E63;
        }

        .loading-more p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        /* Observer Target (invisible) */
        .observer-target {
          height: 20px;
          visibility: hidden;
        }

        /* End of Results */
        .end-results {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          gap: 12px;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.05), rgba(233, 30, 99, 0.03));
          border-radius: 12px;
          margin-top: 20px;
        }

        .end-results i {
          font-size: 40px;
          color: #10b981;
        }

        .end-results p {
          font-size: 14px;
          color: #666;
          margin: 0;
          font-weight: 600;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .modal-container {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 1200px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 2px solid #f0f0f0;
          background: linear-gradient(135deg, rgba(245, 166, 35, 0.1), rgba(233, 30, 99, 0.05));
        }

        .modal-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 18px;
          font-weight: 700;
          color: #333;
          max-width: calc(100% - 60px);
        }

        .modal-title i {
          font-size: 24px;
          color: #E91E63;
          flex-shrink: 0;
        }

        .modal-title span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .modal-close {
          width: 36px;
          height: 36px;
          border: none;
          background: #f0f0f0;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #666;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .modal-close:hover {
          background: #e0e0e0;
          color: #333;
        }

        .modal-close i {
          font-size: 24px;
        }

        .modal-body {
          flex: 1;
          overflow: hidden;
          padding: 0;
        }

        .modal-iframe {
          width: 100%;
          height: calc(90vh - 80px);
          border: none;
        }

        /* Animations */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bx-spin {
          animation: spin 1s linear infinite;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .acts-main-content {
            padding-top: 130px;
          }

          .acts-hero {
            padding: 35px 15px 30px;
          }

          .hero-title {
            font-size: 1.6rem;
          }

          .hero-subtitle {
            font-size: 0.95rem;
          }

          .acts-container {
            margin-top: -20px;
            padding: 0 15px 30px;
          }

          .search-card-header {
            padding: 20px 22px;
          }

          .search-icon-wrapper {
            width: 44px;
            height: 44px;
            font-size: 20px;
          }

          .search-title {
            font-size: 18px;
          }

          .search-card-body {
            padding: 20px 22px;
          }

          .act-card-inner {
            flex-wrap: wrap;
            padding: 16px 18px;
          }

          .act-action {
            width: 100%;
          }

          .view-btn {
            width: 100%;
            justify-content: center;
          }

          .modal-container {
            max-height: 95vh;
          }

          .modal-title {
            font-size: 16px;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.4rem;
          }

          .hero-badge {
            padding: 6px 14px;
            font-size: 12px;
          }

          .search-card-header {
            padding: 18px 20px;
          }

          .search-icon-wrapper {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .search-title {
            font-size: 16px;
          }

          .search-input {
            padding: 14px 45px 14px 50px;
            font-size: 14px;
          }

          .act-icon {
            width: 44px;
            height: 44px;
            font-size: 20px;
          }

          .act-title {
            font-size: 15px;
          }

          .view-btn {
            padding: 10px 16px;
            font-size: 13px;
          }

          .view-btn span {
            display: none;
          }
        }
           /* ============================================
           REMOVE TOP WHITE SPACE
        ============================================ */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default CAActsList;